import { useState } from 'react'
import { useOnboarding } from '../../../contexts/OnboardingContext'
import { MapPin, Building, Wifi, Map } from 'lucide-react'

export function LocationSetupStep() {
  const { data, updateData, nextStep, previousStep } = useOnboarding()
  const [errors, setErrors] = useState<Record<string, string>>({})
  
  const validate = () => {
    const newErrors: Record<string, string> = {}
    
    if (!data.locationName.trim()) {
      newErrors.locationName = 'Location name is required'
    }
    
    if (data.locationType === 'physical' && !data.address?.trim()) {
      newErrors.address = 'Address is required for physical locations'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      nextStep()
    }
  }
  
  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Set up your first location
        </h2>
        <p className="text-gray-600">
          Add where your team will be clocking in and out
        </p>
      </div>
      
      <div className="space-y-6">
        {/* Location Name */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
            <MapPin className="w-4 h-4 mr-2" />
            Location Name *
          </label>
          <input
            type="text"
            value={data.locationName}
            onChange={(e) => updateData({ locationName: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.locationName ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Main Office, Downtown Store, Construction Site A"
          />
          {errors.locationName && (
            <p className="mt-1 text-sm text-red-600">{errors.locationName}</p>
          )}
        </div>
        
        {/* Location Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Location Type *
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => updateData({ locationType: 'physical' })}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                data.locationType === 'physical'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center mb-2">
                <Building className="w-5 h-5 mr-2 text-blue-600" />
                <span className="font-medium">Physical Location</span>
              </div>
              <p className="text-sm text-gray-600">
                Office, store, or specific work site
              </p>
            </button>
            
            <button
              type="button"
              onClick={() => updateData({ locationType: 'remote' })}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                data.locationType === 'remote'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center mb-2">
                <Wifi className="w-5 h-5 mr-2 text-blue-600" />
                <span className="font-medium">Remote Work</span>
              </div>
              <p className="text-sm text-gray-600">
                Work from home or anywhere
              </p>
            </button>
          </div>
        </div>
        
        {/* Address (only for physical locations) */}
        {data.locationType === 'physical' && (
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Map className="w-4 h-4 mr-2" />
              Address *
            </label>
            <input
              type="text"
              value={data.address || ''}
              onChange={(e) => updateData({ address: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.address ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="123 Main St, City, State 12345"
            />
            {errors.address && (
              <p className="mt-1 text-sm text-red-600">{errors.address}</p>
            )}
          </div>
        )}
        
        {/* Geofence option (for physical locations) */}
        {data.locationType === 'physical' && (
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Geofencing</h4>
            <p className="text-sm text-gray-600 mb-3">
              Ensure employees can only clock in when they're at the correct location
            </p>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={!!data.geofence}
                onChange={(e) => {
                  if (e.target.checked) {
                    updateData({
                      geofence: {
                        type: 'circle',
                        data: { radius: 100 } // Default 100m radius
                      }
                    })
                  } else {
                    updateData({ geofence: undefined })
                  }
                }}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                Enable geofencing for this location
              </span>
            </label>
          </div>
        )}
      </div>
      
      {/* Navigation */}
      <div className="mt-8 flex justify-between">
        <button
          type="button"
          onClick={previousStep}
          className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium"
        >
          Back
        </button>
        <button
          type="submit"
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          Continue
        </button>
      </div>
    </form>
  )
}

export default LocationSetupStep